<template>
  <div class="user-form">
    <toolbar
      backRoute="/dashboard/users"
      :beforeBack="checkForChanges"
      :title="toolbarTitle"
      :deleteButtonLabel="deleteButtonLabel"
      deleteButtonEvent="user-item-delete"
      saveButtonLabel="Save user"
      saveButtonEvent="user-item-save"
      :deleteInProgress="removeInProgress"
      :saveInProgress="saveInProgress" />

    <d-container fluid class="common-form px-0 pt-5 pb-5">
      <d-form>
        <validation-observer ref="user-form" tag="form" v-if="!loadError && allDataLoaded">
          <d-row>
            <d-col cols="6" class="ml-auto mr-auto">
              <div class="form-group">
                <label for="name">Username:</label>

                <validation-provider
                  rules="required"
                  name="name"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.name"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">E-mail:</label>

                <validation-provider
                  rules="email"
                  name="email"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.email"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="email">Type:</label>

                <div :class="{ 'form-control': true, 'is-select': true }">
                  <v-select
                    :options="[{ text: 'Administrator', value: 'ADMIN' }, { text: 'User', value: 'USER' }]"
                    v-model="data.type"
                    :reduce="option => option.value"
                    label="text"
                    :clearable="false">
                  </v-select>
                </div>
              </div>

              <div class="form-group">
                <label for="password">Password:</label>

                <validation-provider
                  v-if="!isEdit"
                  rules="required|strong_password"
                  name="password"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.password"
                    type="password"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>

                <validation-provider
                  v-if="isEdit"
                  rules="strong_password"
                  name="password"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.password"
                    type="password"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>

              <div class="form-group">
                <label for="password_confirmation">Confirm password:</label>

                <validation-provider
                  v-if="!isEdit"
                  rules="required|password:password|strong_password"
                  name="password_confirmation"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.password_confirmation"
                    type="password"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>

                <validation-provider
                  v-if="isEdit"
                  rules="password:password|strong_password"
                  name="password_confirmation"
                  v-slot="{ errors }">
                  <d-input
                    v-model="data.password_confirmation"
                    type="password"
                    :state="errors.length ? 'invalid' : null" />
                  <div class="invalid-feedback">
                    {{ errors[0] }}
                  </div>
                </validation-provider>
              </div>
            </d-col>
          </d-row>
        </validation-observer>

        <div v-if="!allDataLoaded" class="pb-3 pt-3 text-center overlay-loading">
          Loading data&hellip;
        </div>

        <d-alert
          v-if="loadError"
          show
          theme="warning">
          The app were unable to load data. <a href="javascript:window.location.reload();" class="alert-link">Refresh the app</a> to try again.
        </d-alert>
      </d-form>
    </d-container>
  </div>
</template>

<script>
import FormUtils from './../../utils/FormUtils.js';
import HasChangesDetection from './../../mixins/HasChangesDetection.vue';
import ListUtils from './../../utils/ListUtils.js';

export default {
  name: 'user-form',
  mixins: [
    HasChangesDetection
  ],
  computed: {
    allDataLoaded () {
      return this.loadedData;
    },
    isEdit () {
      return this.$route.params.id !== 'new' && parseInt(this.$route.params.id, 10) > 0;
    },
    toolbarTitle () {
      if (!this.isEdit) {
        return 'Add new user';
      }

      return 'Edit user';
    },
    deleteButtonLabel () {
      if (this.$route.params.id === 'new') {
        return false;
      }

      return 'Delete user';
    }
  },
  data () {
    return {
      data: {
        id: 0,
        name: '',
        email: '',
        password: '',
        password_confirmation: '',
        type: 'USER'
      },
      loadError: false,
      loadedData: false,
      removeInProgress: false,
      saveInProgress: false
    };
  },
  mounted () {
    if (!this.isEdit) {
      this.loadedData = true;
    } else {
      this.loadData();
    }

    this.$bus.$on('user-item-delete', this.removePassenger);
    this.$bus.$on('user-item-save', this.validateForm);
  },
  methods: {
    loadData () {
      FormUtils.loadItemData(this, {
        endpointBase: '/users/item/',
        instanceFields: ['id', 'name', 'email', 'type'],
        loadedKey: 'loadedData'
      });
    },
    removePassenger () {
      this.removeInProgress = true;

      ListUtils.removeItem({
        $http: this.$http,
        endpointBase: '/users/delete/',
        id: parseInt(this.$route.params.id, 10),
        successTitle: 'User removed',
        successText: 'The selected user has been removed',
        confirmTitle: 'Do you really want to remove this user?',
        confirmText: 'This operation cannot be reverted',
        errorTitle: 'An error occurred',
        errorText: 'Please try again.',
        successAction: () => {
          this.removeInProgress = false;
          this.$router.push('/dashboard/users');
        },
        cancelAction: () => {
          this.removeInProgress = false;
        }
      });
    },
    validateForm () {
      FormUtils.validate(this.$refs['user-form'], this.submitForm);
    },
    submitForm () {
      let endpointUrl = '/users/store';
      let mode = 'add';

      if (this.data.id > 0) {
        endpointUrl = '/users/update/' + parseInt(this.data.id, 10);
        mode = 'edit';
      }

      FormUtils.submit({
        $http: this.$http,
        endpoint: endpointUrl,
        formData: this.data,
        successTitle: mode === 'add' ? 'User added' : 'User saved',
        successText: 'The user has been successfully saved!',
        errorTitle: 'An error occurred!',
        errorText: 'The operation not finished successfully. Please try again.',
        successAction: () => {
          this.$router.push('/dashboard/users');
        }
      });
    }
  },
  beforeDestroy () {
    this.$bus.$off('user-item-delete', this.removeUser);
    this.$bus.$off('user-item-save', this.validateForm);
  }
}
</script>
